// Types and formatting for the Operators view under Performance.
//
// One rule governs every function in this file: a figure that is not known is
// rendered as words saying it is not known, never as a zero and never as a dash
// that could be mistaken for one. CLAUDE.md section 1.6. On this screen it
// matters more than most, because a zero against somebody's name reads as a
// judgement about them.

export type Slot = 'primary' | 'supporting' | 'constraint'
export type Grain = 'day' | 'week' | 'month'

export type NumberState = 'declared, not measured' | 'bound, no values yet' | 'measured'

export type ScoreboardRow = {
  person_id: string
  person_name: string
  instruction_version: number
  metric_id: string
  slot: Slot
  slot_index: number
  name: string
  unit: string | null
  direction: 'higher_is_better' | 'lower_is_better' | 'watch_only'
  effective_target: number | null
  number_state: NumberState
  declared_status: string | null
  latest_grain: Grain | null
  latest_period: string | null
  latest_value: number | null
  latest_is_partial: boolean | null
  latest_on_target: boolean | null
  // Read off servant_roles. 'no_role' is a real answer, not a placeholder: five
  // of the ten people with a cockpit login have no recorded role at all.
  division: string
  roles: string
}

export type InstructionRow = {
  instruction_id: string
  person_id: string
  person_name: string
  person_email: string | null
  version: number
  role_label: string
  outcome_statement: string | null
  parse_status: 'parsed' | 'partial' | 'unreadable'
  parse_warnings: string[]
  parse_warning_count: number
  pasted_at: string
  accepted_at: string | null
  is_accepted: boolean
  metrics_declared: number
  metrics_bound: number
}

export type MissingRow = {
  person_id: string | null
  email: string
  person_name: string
  active_roles: string | null
  instruction_versions_ever: number
  instruction_state: string
}

export type NumberStatusRow = {
  metric_id: string
  person_id: string
  person_name: string
  instruction_version: number
  slot: Slot
  slot_index: number
  name: string
  definition_text: string | null
  cadence: 'daily' | 'weekly' | 'monthly' | 'quarterly' | null
  direction: string
  unit: string | null
  target_value: number | null
  target_text: string | null
  declared_status: string | null
  measure_key: string | null
  measure_label: string | null
  measure_scope: 'person' | 'fleet' | null
  measure_source_note: string | null
  effective_target: number | null
  binding_rationale: string | null
  is_bound: boolean
  measured_periods: number
  number_state: NumberState
}

export type MeasureRow = {
  measure_key: string
  label: string
  scope: 'person' | 'fleet'
  unit: string
  direction: string
  grains: Grain[]
  source_note: string
  default_target: number | null
}

export type OperatorBoard = {
  scoreboard: ScoreboardRow[]
  instructions: InstructionRow[]
  missing: MissingRow[]
  numbers: NumberStatusRow[]
  measures: MeasureRow[]
  last_computed_at: string | null
}

export type SeriesRow = {
  metric_id: string
  person_id: string | null
  person_name: string
  slot: Slot
  name: string
  unit: string | null
  measure_key: string
  measure_label: string
  effective_target: number | null
  grain: Grain
  period_start: string
  value: number | null
  is_partial_period: boolean
  variance: number | null
  on_target: boolean | null
  computed_at: string
}

export type ParsedMetric = {
  slot: Slot
  slot_index: number
  name: string
  definition_text: string | null
  cadence: string | null
  direction: string
  unit: string | null
  target_value: number | null
  target_text: string | null
  declared_status: string | null
}

export type ParseResult = {
  status: 'parsed' | 'partial' | 'unreadable'
  role_label: string | null
  outcome_statement: string | null
  metrics: ParsedMetric[]
  warnings: string[]
}

export const NOT_MEASURED = 'not measured'

const ZA = 'en-ZA'

// Deliberately the SAME Intl options as randFmt in utils/performance.ts, so the
// two views under one tab do not group thousands differently. en-ZA groups with a
// no-break space, so a figure reads "R 9 252" and not "R 9,252", matching every
// number on the Fleet view next door.
//
// Not imported from there, and the reason is mechanical rather than principled:
// the web tests run under node's type stripping, which needs an explicit .ts on
// an import, while the web tsconfig refuses one without
// allowImportingTsExtensions. So operator.ts stays free of cross-module src
// imports and formatsAgreeWithPerformance in
// web/test/operatorNoZeroForUnknown.test.ts asserts the two produce identical
// output, which is the property that actually matters.
const moneyFmt = new Intl.NumberFormat(ZA, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
const intFmt = new Intl.NumberFormat(ZA)

function rand(value: number): string {
  const rounded = Math.round(value)
  const sign = rounded < 0 ? '-' : ''
  return `${sign}R ${moneyFmt.format(Math.abs(rounded))}`
}

/**
 * Render a measured value in its own unit. null is the words, never a zero.
 */
export function formatValue(value: number | null, unit: string | null): string {
  if (value === null || !Number.isFinite(value)) return NOT_MEASURED
  switch (unit) {
    case 'zar':
      return rand(value)
    case 'percent':
      return `${value.toFixed(1)}%`
    case 'l_per_100km':
      return `${value.toFixed(2)} L/100km`
    case 'months':
      return value === 1 ? '1 month' : `${value.toFixed(1)} months`
    // Added with the operations measures in 0100. Nothing in the catalogue
    // answered in a length of time before then, and a duration rendered as a
    // bare number reads as a count of something.
    case 'hours':
      return value === 1 ? '1 hour' : `${value.toFixed(1)} hours`
    // Added with 0111. Downtime per incident and incident resolution both answer
    // in days, and a duration shown as a bare number reads as a count.
    case 'days':
      return value === 1 ? '1 day' : `${value.toFixed(1)} days`
    case 'count':
      return intFmt.format(Math.round(value))
    default:
      // No unit was ever set on this number. Show the figure plainly rather than
      // guessing a currency onto it.
      return Number.isInteger(value) ? intFmt.format(value) : value.toFixed(2)
  }
}

export function formatTarget(row: { effective_target: number | null; unit: string | null }): string {
  if (row.effective_target === null) return 'no target set'
  return formatValue(row.effective_target, row.unit)
}

/**
 * on_target is deliberately three-valued. NULL means either the figure or the
 * target is unknown, and that is not the same as failing.
 */
export function targetVerdict(row: {
  latest_value: number | null
  latest_on_target: boolean | null
}): 'good' | 'bad' | 'unknown' {
  if (row.latest_value === null || row.latest_on_target === null) return 'unknown'
  return row.latest_on_target ? 'good' : 'bad'
}

export function verdictClass(v: 'good' | 'bad' | 'unknown'): string {
  if (v === 'good') return 'text-emerald-700 dark:text-emerald-400'
  if (v === 'bad') return 'text-red-700 dark:text-red-400'
  return 'text-muted-foreground'
}

export function stateClass(state: NumberState): string {
  if (state === 'measured') return 'text-emerald-700 dark:text-emerald-400'
  if (state === 'bound, no values yet') return 'text-amber-700 dark:text-amber-400'
  return 'text-muted-foreground'
}

export function slotLabel(slot: Slot): string {
  if (slot === 'primary') return 'Primary'
  if (slot === 'constraint') return 'Constraint'
  return 'Supporting'
}

export function slotOrder(slot: Slot): number {
  if (slot === 'primary') return 0
  if (slot === 'constraint') return 1
  return 2
}

/**
 * A period label that says which bucket it is, so a week is never mistaken for a
 * month on a chart axis.
 */
export function formatPeriod(period: string, grain: Grain): string {
  if (!period) return ''
  // Take the date part of whatever arrived. A Postgres `date` column comes
  // through postgres.js as a Date and serialises as "2026-06-01T00:00:00.000Z";
  // appending "T00:00:00Z" to that produced an invalid date, the guard below
  // caught it, and the screen printed the raw timestamp as a column header. The
  // handler now casts to text as well; this is the second line.
  const day = /^(\d{4}-\d{2}-\d{2})/.exec(String(period))?.[1]
  if (!day) return String(period)
  const d = new Date(`${day}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return String(period)
  if (grain === 'month') {
    return d.toLocaleDateString(ZA, { month: 'short', year: 'numeric', timeZone: 'UTC' })
  }
  if (grain === 'week') {
    return `w/c ${d.toLocaleDateString(ZA, { day: '2-digit', month: 'short', timeZone: 'UTC' })}`
  }
  return d.toLocaleDateString(ZA, { day: '2-digit', month: 'short', timeZone: 'UTC' })
}

export function formatWhen(iso: string | null): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'never'
  return d.toLocaleString(ZA, { dateStyle: 'medium', timeStyle: 'short' })
}

/**
 * Group the scoreboard by person, primary first, then constraint, then
 * supporting. The order is not cosmetic: a screen that lists a person's numbers
 * alphabetically buries the one figure the role is judged on.
 */
export function byPerson(rows: ScoreboardRow[]): Array<{
  person_id: string
  person_name: string
  instruction_version: number
  primary: ScoreboardRow | null
  others: ScoreboardRow[]
}> {
  const map = new Map<string, ScoreboardRow[]>()
  for (const row of rows) {
    const list = map.get(row.person_id) ?? []
    list.push(row)
    map.set(row.person_id, list)
  }
  return [...map.entries()]
    .map(([person_id, list]) => {
      const sorted = [...list].sort(
        (a, b) => slotOrder(a.slot) - slotOrder(b.slot) || a.slot_index - b.slot_index,
      )
      const primary = sorted.find((r) => r.slot === 'primary') ?? null
      return {
        person_id,
        person_name: sorted[0]?.person_name ?? 'Unknown',
        instruction_version: sorted[0]?.instruction_version ?? 0,
        primary,
        others: sorted.filter((r) => r.slot !== 'primary'),
      }
    })
    .sort((a, b) => a.person_name.localeCompare(b.person_name))
}
